import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Eye,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle2,
  Circle,
  Square,
  ToggleLeft,
  Type,
  FileText,
  Settings,
  Loader2,
  Copy,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAssessment } from '@/api/hooks';
import type { AssessmentQuestion } from '@/api/client';

export const Route = createFileRoute('/author/assessments/$assessmentId/edit')({
  component: QuizBuilderPage,
});

type QuestionType = 'multiple_choice' | 'multiple_select' | 'true_false' | 'short_answer' | 'essay';

interface EditableQuestion {
  id: string;
  type: QuestionType;
  question: string;
  options: { value: string; label: string }[];
  correct_answer: unknown;
  points: number;
  explanation: string;
  isNew?: boolean;
}

interface EditableAssessment {
  name: string;
  description: string;
  instructions: string;
  type: 'quiz' | 'exam' | 'practice' | 'survey';
  passing_score: number;
  time_limit: number | null;
  max_attempts: number | null;
  shuffle_questions: boolean;
  shuffle_answers: boolean;
  show_correct_answers: boolean;
  questions: EditableQuestion[];
}

const questionTypeIcons: Record<QuestionType, typeof Circle> = {
  multiple_choice: Circle,
  multiple_select: Square,
  true_false: ToggleLeft,
  short_answer: Type,
  essay: FileText,
};

const questionTypeLabels: Record<QuestionType, string> = {
  multiple_choice: 'Multiple Choice',
  multiple_select: 'Multiple Select',
  true_false: 'True/False',
  short_answer: 'Short Answer',
  essay: 'Essay',
};

function QuizBuilderPage() {
  const { assessmentId } = Route.useParams();
  const isNew = assessmentId === 'new';

  const { data: existingAssessment, isLoading } = useAssessment(isNew ? '' : assessmentId);

  const [assessment, setAssessment] = useState<EditableAssessment>({
    name: '',
    description: '',
    instructions: '',
    type: 'quiz',
    passing_score: 70,
    time_limit: null,
    max_attempts: null,
    shuffle_questions: false,
    shuffle_answers: false,
    show_correct_answers: true,
    questions: [],
  });

  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing assessment
  useEffect(() => {
    if (existingAssessment && !isNew) {
      setAssessment({
        name: existingAssessment.name || '',
        description: existingAssessment.description || '',
        instructions: existingAssessment.instructions || '',
        type: existingAssessment.type || 'quiz',
        passing_score: existingAssessment.passing_score || 70,
        time_limit: existingAssessment.time_limit,
        max_attempts: existingAssessment.max_attempts,
        shuffle_questions: existingAssessment.shuffle_questions || false,
        shuffle_answers: existingAssessment.shuffle_answers || false,
        show_correct_answers: existingAssessment.show_correct_answers ?? true,
        questions: (existingAssessment.questions || []).map((q) => ({
          id: q.id,
          type: q.type as QuestionType,
          question: q.question,
          options: (q.options as { value: string; label: string }[]) || [],
          correct_answer: q.correct_answer,
          points: q.points,
          explanation: q.explanation || '',
        })),
      });
    }
  }, [existingAssessment, isNew]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement save API call
      console.log('Saving assessment:', assessment);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = <K extends keyof EditableAssessment>(
    field: K,
    value: EditableAssessment[K]
  ) => {
    setAssessment((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleAddQuestion = (type: QuestionType) => {
    const newQuestion: EditableQuestion = {
      id: `new-${Date.now()}`,
      type,
      question: '',
      options:
        type === 'multiple_choice' || type === 'multiple_select'
          ? [
              { value: 'a', label: 'Option A' },
              { value: 'b', label: 'Option B' },
              { value: 'c', label: 'Option C' },
              { value: 'd', label: 'Option D' },
            ]
          : type === 'true_false'
          ? [
              { value: 'true', label: 'True' },
              { value: 'false', label: 'False' },
            ]
          : [],
      correct_answer: type === 'multiple_select' ? [] : '',
      points: 1,
      explanation: '',
      isNew: true,
    };
    setAssessment((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
    setSelectedQuestionId(newQuestion.id);
    setHasChanges(true);
  };

  const handleQuestionChange = (
    questionId: string,
    field: keyof EditableQuestion,
    value: unknown
  ) => {
    setAssessment((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId ? { ...q, [field]: value } : q
      ),
    }));
    setHasChanges(true);
  };

  const handleDeleteQuestion = (questionId: string) => {
    setAssessment((prev) => ({
      ...prev,
      questions: prev.questions.filter((q) => q.id !== questionId),
    }));
    if (selectedQuestionId === questionId) {
      setSelectedQuestionId(null);
    }
    setHasChanges(true);
  };

  const handleDuplicateQuestion = (question: EditableQuestion) => {
    const duplicate: EditableQuestion = {
      ...question,
      id: `new-${Date.now()}`,
      isNew: true,
    };
    setAssessment((prev) => ({
      ...prev,
      questions: [...prev.questions, duplicate],
    }));
    setSelectedQuestionId(duplicate.id);
    setHasChanges(true);
  };

  const handleOptionChange = (
    questionId: string,
    optionIdx: number,
    value: string
  ) => {
    const question = assessment.questions.find((q) => q.id === questionId);
    if (!question) return;

    const newOptions = [...question.options];
    newOptions[optionIdx] = { ...newOptions[optionIdx], label: value };

    handleQuestionChange(questionId, 'options', newOptions);
  };

  const handleAddOption = (questionId: string) => {
    const question = assessment.questions.find((q) => q.id === questionId);
    if (!question) return;

    const nextLetter = String.fromCharCode(97 + question.options.length);
    const newOptions = [
      ...question.options,
      { value: nextLetter, label: `Option ${nextLetter.toUpperCase()}` },
    ];

    handleQuestionChange(questionId, 'options', newOptions);
  };

  const handleRemoveOption = (questionId: string, optionIdx: number) => {
    const question = assessment.questions.find((q) => q.id === questionId);
    if (!question || question.options.length <= 2) return;

    const newOptions = question.options.filter((_, idx) => idx !== optionIdx);
    handleQuestionChange(questionId, 'options', newOptions);
  };

  const selectedQuestion = assessment.questions.find((q) => q.id === selectedQuestionId);
  const totalPoints = assessment.questions.reduce((sum, q) => sum + q.points, 0);

  if (isLoading && !isNew) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/author"
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isNew ? 'Create Assessment' : 'Edit Assessment'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {assessment.questions.length} questions · {totalPoints} points total
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/learn/assessments/$assessmentId"
            params={{ assessmentId }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Link>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left sidebar - Settings & Question List */}
        <div className="space-y-6">
          {/* Assessment Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Name</label>
                <input
                  type="text"
                  value={assessment.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                  placeholder="Assessment name"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Type</label>
                  <select
                    value={assessment.type}
                    onChange={(e) =>
                      handleFieldChange('type', e.target.value as EditableAssessment['type'])
                    }
                    className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                  >
                    <option value="quiz">Quiz</option>
                    <option value="exam">Exam</option>
                    <option value="practice">Practice</option>
                    <option value="survey">Survey</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Pass %</label>
                  <input
                    type="number"
                    value={assessment.passing_score}
                    onChange={(e) => handleFieldChange('passing_score', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                    min={0}
                    max={100}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Time (min)</label>
                  <input
                    type="number"
                    value={assessment.time_limit || ''}
                    onChange={(e) =>
                      handleFieldChange('time_limit', e.target.value ? parseInt(e.target.value) : null)
                    }
                    className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                    placeholder="No limit"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Attempts</label>
                  <input
                    type="number"
                    value={assessment.max_attempts || ''}
                    onChange={(e) =>
                      handleFieldChange('max_attempts', e.target.value ? parseInt(e.target.value) : null)
                    }
                    className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assessment.shuffle_questions}
                    onChange={(e) => handleFieldChange('shuffle_questions', e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm">Shuffle questions</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assessment.shuffle_answers}
                    onChange={(e) => handleFieldChange('shuffle_answers', e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm">Shuffle answers</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assessment.show_correct_answers}
                    onChange={(e) => handleFieldChange('show_correct_answers', e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm">Show correct answers</span>
                </label>
              </div>
            </div>
          </motion.div>

          {/* Question List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <h2 className="font-semibold text-foreground mb-4">Questions</h2>

            <div className="space-y-2 mb-4">
              {assessment.questions.map((q, idx) => {
                const Icon = questionTypeIcons[q.type];
                return (
                  <button
                    key={q.id}
                    onClick={() => setSelectedQuestionId(q.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      selectedQuestionId === q.id
                        ? 'border-indigo-500 bg-indigo-500/20'
                        : 'border-border hover:border-input hover:bg-secondary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{idx + 1}</span>
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm truncate flex-1">
                        {q.question || 'Untitled question'}
                      </span>
                      <span className="text-xs text-muted-foreground">{q.points}pt</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Add Question */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Add question:</p>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(questionTypeLabels) as QuestionType[]).map((type) => {
                  const Icon = questionTypeIcons[type];
                  return (
                    <button
                      key={type}
                      onClick={() => handleAddQuestion(type)}
                      className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-dashed border-border hover:border-indigo-500 hover:bg-indigo-500/10 transition-colors"
                    >
                      <Icon className="w-3 h-3" />
                      {questionTypeLabels[type]}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main content - Question Editor */}
        <div className="lg:col-span-2">
          {selectedQuestion ? (
            <motion.div
              key={selectedQuestion.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  {(() => {
                    const Icon = questionTypeIcons[selectedQuestion.type];
                    return <Icon className="w-5 h-5 text-indigo-500" />;
                  })()}
                  <span className="font-medium text-foreground">
                    {questionTypeLabels[selectedQuestion.type]}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDuplicateQuestion(selectedQuestion)}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => handleDeleteQuestion(selectedQuestion.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>

              {/* Question text */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Question
                </label>
                <textarea
                  value={selectedQuestion.question}
                  onChange={(e) =>
                    handleQuestionChange(selectedQuestion.id, 'question', e.target.value)
                  }
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all resize-none"
                  placeholder="Enter your question..."
                />
              </div>

              {/* Options for multiple choice / select */}
              {(selectedQuestion.type === 'multiple_choice' ||
                selectedQuestion.type === 'multiple_select') && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Answer Options
                  </label>
                  <div className="space-y-2">
                    {selectedQuestion.options.map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            if (selectedQuestion.type === 'multiple_choice') {
                              handleQuestionChange(
                                selectedQuestion.id,
                                'correct_answer',
                                opt.value
                              );
                            } else {
                              const current = (selectedQuestion.correct_answer as string[]) || [];
                              if (current.includes(opt.value)) {
                                handleQuestionChange(
                                  selectedQuestion.id,
                                  'correct_answer',
                                  current.filter((v) => v !== opt.value)
                                );
                              } else {
                                handleQuestionChange(
                                  selectedQuestion.id,
                                  'correct_answer',
                                  [...current, opt.value]
                                );
                              }
                            }
                          }}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            selectedQuestion.type === 'multiple_choice'
                              ? selectedQuestion.correct_answer === opt.value
                                ? 'border-green-500 bg-green-500'
                                : 'border-muted-foreground hover:border-green-500'
                              : (selectedQuestion.correct_answer as string[])?.includes(opt.value)
                              ? 'border-green-500 bg-green-500'
                              : 'border-muted-foreground hover:border-green-500'
                          }`}
                        >
                          {(selectedQuestion.type === 'multiple_choice'
                            ? selectedQuestion.correct_answer === opt.value
                            : (selectedQuestion.correct_answer as string[])?.includes(
                                opt.value
                              )) && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </button>
                        <input
                          type="text"
                          value={opt.label}
                          onChange={(e) =>
                            handleOptionChange(selectedQuestion.id, idx, e.target.value)
                          }
                          className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                        />
                        {selectedQuestion.options.length > 2 && (
                          <button
                            onClick={() => handleRemoveOption(selectedQuestion.id, idx)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => handleAddOption(selectedQuestion.id)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Option
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Click the circle to mark the correct answer
                    {selectedQuestion.type === 'multiple_select' && ' (select all that apply)'}
                  </p>
                </div>
              )}

              {/* True/False correct answer */}
              {selectedQuestion.type === 'true_false' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Correct Answer
                  </label>
                  <div className="flex gap-4">
                    {['true', 'false'].map((value) => (
                      <button
                        key={value}
                        onClick={() =>
                          handleQuestionChange(
                            selectedQuestion.id,
                            'correct_answer',
                            value === 'true'
                          )
                        }
                        className={`flex-1 p-4 rounded-lg border text-center font-medium transition-all ${
                          selectedQuestion.correct_answer === (value === 'true')
                            ? 'border-green-500 bg-green-500/20 text-green-400'
                            : 'border-border hover:border-input'
                        }`}
                      >
                        {value === 'true' ? 'True' : 'False'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Points and explanation */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Points
                  </label>
                  <input
                    type="number"
                    value={selectedQuestion.points}
                    onChange={(e) =>
                      handleQuestionChange(
                        selectedQuestion.id,
                        'points',
                        parseInt(e.target.value) || 1
                      )
                    }
                    className="w-full px-4 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none"
                    min={1}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Explanation (shown after answering)
                </label>
                <textarea
                  value={selectedQuestion.explanation}
                  onChange={(e) =>
                    handleQuestionChange(selectedQuestion.id, 'explanation', e.target.value)
                  }
                  rows={2}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 outline-none resize-none"
                  placeholder="Explain why this is the correct answer..."
                />
              </div>
            </motion.div>
          ) : (
            <div className="bg-card rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground mb-4">
                Select a question from the list or add a new one to get started.
              </p>
              <button
                onClick={() => handleAddQuestion('multiple_choice')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add First Question
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
